import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import url from '../serverUrl';

const API_URL = url;

const LOCK_STATUS_OPTIONS = {
  immutability: ['immutable', 'governed', 'upgradeable', 'multisig'],
  withdrawal_permissions: [
    'no_withdrawal_allowed',
    'withdrawal_allowed_by_governance',
    'withdrawal_allowed_by_multisig',
    'withdrawal_allowed_by_protocol_rules'
  ],
  backing_freeze_status: ['frozen', 'dynamic', 'algorithmic'],
  auditability: ['public_on_chain', 'private', 'restricted']
};

const getCurrentUser = () => {
  try {
    const stored = localStorage.getItem('hootdex_secretcookie');
    const parsed = stored ? JSON.parse(stored) : null;
    return { uid: parsed?.user_id ?? null, pub_key: parsed?.pub_key ?? null };
  } catch {
    return { uid: null, pub_key: null };
  }
};

/**
 * Registers a treasury: takes an escrow_public_key (a locker row that's
 * already `lock_status = 1` in Postgres - e.g. from a wallet transfer/lock
 * with a lock_date) and turns it into a treasury record that can then be
 * assigned to exactly one token via the Treasury Backing dropdown on each
 * token's edit form.
 *
 * Lock_status fields are collected here too since a treasury can't be
 * linked to a token until they're set anyway (see
 * commonFunctions/tokenTreasuryLinks.js on the backend).
 */
function CreateTreasuryForm() {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    escrow_public_key: '',
    symbol: '',
    pledgeToken: '',
    immutability: '',
    withdrawal_permissions: '',
    backing_freeze_status: '',
    auditability: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.escrow_public_key) {
      return setAlert({
        open: true,
        severity: 'error',
        message: 'escrow_public_key is required'
      });
    }

    const { uid, pub_key } = getCurrentUser();
    if (!uid || !pub_key) {
      return setAlert({
        open: true,
        severity: 'error',
        message:
          'Could not find your uid/pub_key from your session - please log in again.'
      });
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/v2/treasury/add`, {
        ...formData,
        uid,
        pub_key
      });

      setAlert({
        open: true,
        severity: 'success',
        message: `Treasury created: ${res.data.treasury_key}`
      });

      setFormData({
        escrow_public_key: '',
        symbol: '',
        pledgeToken: '',
        immutability: '',
        withdrawal_permissions: '',
        backing_freeze_status: '',
        auditability: ''
      });
    } catch (err) {
      setAlert({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || 'Failed to create treasury'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" mt={4} mb={4}>
      <Paper sx={{ p: 4, maxWidth: 640, width: '100%' }}>
        <Typography variant="h5" gutterBottom>
          Create Treasury
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          The escrow public key must already be a locked escrow (lock_status
          = 1 in the locker table) - e.g. from a wallet transfer with a lock
          date. This form registers it as a treasury so it can then be
          assigned to a token from that token&apos;s edit screen.
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            required
            label="Escrow Public Key"
            name="escrow_public_key"
            value={formData.escrow_public_key}
            onChange={handleChange}
            margin="normal"
            helperText="The pub_key of the locked escrow this treasury represents"
          />
          <TextField
            fullWidth
            label="Symbol (optional)"
            name="symbol"
            value={formData.symbol}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Pledge Token (optional)"
            name="pledgeToken"
            value={formData.pledgeToken}
            onChange={handleChange}
            margin="normal"
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Lock Status (required before this treasury can back a token)
          </Typography>

          {Object.entries(LOCK_STATUS_OPTIONS).map(([field, options]) => (
            <TextField
              key={field}
              select
              fullWidth
              label={field
                .split('_')
                .map((w) => w[0].toUpperCase() + w.slice(1))
                .join(' ')}
              name={field}
              value={formData[field]}
              onChange={handleChange}
              margin="normal"
            >
              <MenuItem value="">
                <em>Not set yet</em>
              </MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          ))}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, bgcolor: 'darkgreen' }}
          >
            {loading ? 'Creating...' : 'Create Treasury'}
          </Button>
        </form>
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default CreateTreasuryForm;
